import { useEffect, useMemo, useState } from "react";
import type { UiCommandBusV2, UiSnapshotV2 } from "@/ui/runtime/contracts/manager-ui-v2";
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/foundation/primitives/avatar";
import { Badge } from "@/ui/foundation/primitives/badge";
import { Button } from "@/ui/foundation/primitives/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/foundation/primitives/card";
import { DataList } from "@/ui/components/common/data-list";
import { AttributeScoreBar } from "@/ui/components/common/attribute-score-bar";
import { ManagerSecondaryPane } from "@/ui/components/manager/manager-layout";
import { avatarDataUri, formatMoney, initials, renderCompactPips } from "@/ui/components/manager/manager-ui-helpers";
import { BanIcon, CircleXIcon, FlagIcon } from "lucide-react";

interface UseSquadScreenStateResult {
    selectedPlayer: UiSnapshotV2["routes"]["squad"]["players"][number];
    filteredSquadPlayers: UiSnapshotV2["routes"]["squad"]["players"];
    squadSearch: string;
    trainingPlanDraft: string;
    trainingIntensityDraft: number;
    salePriceDraft: number;
    setSquadSearch(value: string): void;
    selectPlayer(playerId: number): void;
    setTrainingPlanDraft(value: string): void;
    setTrainingIntensityDraft(value: number): void;
    setSalePriceDraft(value: number): void;
}

export function useSquadScreenState(snapshot: UiSnapshotV2): UseSquadScreenStateResult {
    const squadPlayers = snapshot.routes.squad.players;
    const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
    const [trainingPlanDraft, setTrainingPlanDraft] = useState<string>("balanced");
    const [trainingIntensityDraft, setTrainingIntensityDraft] = useState<number>(2);
    const [salePriceDraft, setSalePriceDraft] = useState<number>(2500000);
    const [squadSearch, setSquadSearch] = useState<string>("");

    useEffect(() => {
        if (!selectedPlayerId && squadPlayers[0]) {
            setSelectedPlayerId(squadPlayers[0].id);
        }
    }, [selectedPlayerId, squadPlayers]);

    const selectedPlayer = useMemo(
        () => squadPlayers.find((player) => player.id === selectedPlayerId) ?? squadPlayers[0] ?? {
            id: 0,
            name: "No Player",
            role: "N/A",
            readiness: 0,
            rating: 0,
            morale: "N/A",
            keySkills: { pace: 0, passing: 0, tackling: 0, finishing: 0 },
        },
        [squadPlayers, selectedPlayerId]
    );

    useEffect(() => {
        const listedPrice = Number(selectedPlayer.listedTargetPrice ?? 0);
        if (listedPrice > 0) {
            setSalePriceDraft(Math.round(listedPrice));
        } else {
            const seed = (selectedPlayer.keySkills?.pace ?? 50) * 10000 + (selectedPlayer.rating * 5000);
            setSalePriceDraft(Math.max(500000, seed));
        }
        setTrainingPlanDraft("balanced");
        setTrainingIntensityDraft(2);
    }, [selectedPlayer.id, selectedPlayer.keySkills?.pace, selectedPlayer.rating, selectedPlayer.listedTargetPrice]);

    const filteredSquadPlayers = useMemo(() => {
        const query = squadSearch.trim().toLowerCase();
        if (!query) {
            return squadPlayers;
        }
        return squadPlayers.filter((player) =>
            `${player.name} ${player.role} ${player.morale}`.toLowerCase().includes(query)
        );
    }, [squadPlayers, squadSearch]);

    return {
        selectedPlayer,
        filteredSquadPlayers,
        squadSearch,
        trainingPlanDraft,
        trainingIntensityDraft,
        salePriceDraft,
        setSquadSearch,
        selectPlayer: setSelectedPlayerId,
        setTrainingPlanDraft,
        setTrainingIntensityDraft,
        setSalePriceDraft,
    };
}

interface SquadScreenSecondaryProps {
    state: UseSquadScreenStateResult;
    commands: UiCommandBusV2;
}

export function SquadScreenSecondary({ state, commands }: SquadScreenSecondaryProps) {
    return (
        <ManagerSecondaryPane
            title="Squad"
            search={{
                value: state.squadSearch,
                onChange: state.setSquadSearch,
            }}
        >
            <DataList
                items={state.filteredSquadPlayers}
                getKey={(player) => player.id}
                selectedKey={state.selectedPlayer.id}
                onSelect={(player) => {
                    state.selectPlayer(player.id);
                    void commands.squadSelectPlayer(player.id);
                }}
                itemClassName="flex items-center gap-3"
                renderItem={(player) => (
                    <>
                        <Avatar className="size-9">
                            <AvatarImage alt={player.name} src={avatarDataUri(player.avatarSvg)} />
                            <AvatarFallback className="text-[10px]">{initials(player.name)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium">{player.name}</div>
                            <div className="text-xs text-muted-foreground">{player.role} · {player.morale}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1 text-xs text-muted-foreground">
                            {player.availability === "suspended" ? (
                                <span className="inline-flex items-center gap-1 text-red-600" title="Unavailable: Suspended">
                                    <BanIcon className="size-3.5" />
                                    <span className="sr-only">Unavailable: Suspended</span>
                                </span>
                            ) : player.availability === "injured" ? (
                                <span className="inline-flex items-center gap-1 text-red-600" title="Unavailable: Injured">
                                    <CircleXIcon className="size-3.5" />
                                    <span className="sr-only">Unavailable: Injured</span>
                                </span>
                            ) : player.readiness < 100 ? (
                                <span className="inline-flex items-center gap-1 text-amber-600" title={`Readiness ${player.readiness}%`}>
                                    <FlagIcon className="size-3.5" />
                                    <span className="sr-only">Readiness {player.readiness}%</span>
                                </span>
                            ) : null}
                            {renderCompactPips(player.rating)}
                        </div>
                    </>
                )}
            />
        </ManagerSecondaryPane>
    );
}

interface SquadScreenMainProps {
    commands: UiCommandBusV2;
    state: UseSquadScreenStateResult;
}

export function SquadScreenMain({ commands, state }: SquadScreenMainProps) {
    const selectedPlayer = state.selectedPlayer;
    const injuryTypeLabels: Record<"impact" | "muscle" | "knock", string> = {
        impact: "Impact injury",
        muscle: "Muscle injury",
        knock: "Knock"
    };
    const attributeEntries = Object.entries(selectedPlayer.attributes ?? {}).map(([name, value]) => [name, Number(value)] as const);
    const strongestAttribute = attributeEntries.length > 0
        ? attributeEntries.reduce((best, current) => (current[1] > best[1] ? current : best), attributeEntries[0])
        : null;
    const weakestAttribute = attributeEntries.length > 0
        ? attributeEntries.reduce((worst, current) => (current[1] < worst[1] ? current : worst), attributeEntries[0])
        : null;
    const formatAttributeLabel = (label: string): string => label.charAt(0).toUpperCase() + label.slice(1);

    return (
        <div className="grid gap-4 2xl:grid-cols-[2fr_1fr]">
            <Card>
                <CardContent className="flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                        <Avatar className="size-20 rounded-full border">
                            <AvatarImage alt={selectedPlayer.name} src={avatarDataUri(selectedPlayer.avatarSvg)} />
                            <AvatarFallback className="text-base">{initials(selectedPlayer.name)}</AvatarFallback>
                        </Avatar>
                        <div className="space-y-2">
                            <div className="text-xl font-semibold leading-none">{selectedPlayer.name}</div>
                            <div className="flex gap-2">
                                <Badge>{selectedPlayer.role}</Badge>
                                <Badge variant="secondary">Rating {selectedPlayer.rating}</Badge>
                                <Badge variant="outline">Readiness {selectedPlayer.readiness}%</Badge>
                                <Badge variant="outline">Morale {selectedPlayer.morale}</Badge>
                                {selectedPlayer.availability === "injured" ? <Badge variant="destructive">Injured</Badge> : null}
                                {selectedPlayer.availability === "suspended" ? <Badge variant="destructive">Suspended</Badge> : null}
                                {selectedPlayer.availability === "available" && selectedPlayer.readiness < 100 ? (
                                    <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100">Not Fully Fit</Badge>
                                ) : null}
                                {selectedPlayer.listedForTransfer ? (
                                    <Badge variant="destructive">
                                        Listed {selectedPlayer.listedTargetPrice ? formatMoney(selectedPlayer.listedTargetPrice) : ""}
                                    </Badge>
                                ) : null}
                            </div>
                            {selectedPlayer.availability === "injured" && selectedPlayer.injuryStatus ? (
                                <div className="text-sm text-red-700">
                                    {injuryTypeLabels[selectedPlayer.injuryStatus.type]} (severity {selectedPlayer.injuryStatus.severity}) ·
                                    Out until matchday {selectedPlayer.injuryStatus.outUntilMatchday}
                                    {selectedPlayer.injuryStatus.remainingMatches > 0
                                        ? ` (${selectedPlayer.injuryStatus.remainingMatches} match${selectedPlayer.injuryStatus.remainingMatches === 1 ? "" : "es"} remaining)`
                                        : ""}
                                </div>
                            ) : null}
                            {selectedPlayer.availability === "suspended" && selectedPlayer.suspensionStatus ? (
                                <div className="text-sm text-red-700">
                                    Suspended until matchday {selectedPlayer.suspensionStatus.suspendedUntilMatchday}
                                    {selectedPlayer.suspensionStatus.remainingMatches > 0
                                        ? ` (${selectedPlayer.suspensionStatus.remainingMatches} match${selectedPlayer.suspensionStatus.remainingMatches === 1 ? "" : "es"} remaining)`
                                        : ""}
                                </div>
                            ) : null}
                            {selectedPlayer.availability === "available" && selectedPlayer.readiness < 100 ? (
                                <div className="text-sm text-amber-700">
                                    Not fully fit: current readiness {selectedPlayer.readiness}%.
                                </div>
                            ) : null}
                            {selectedPlayer.availability === "available" && selectedPlayer.readiness >= 100 ? (
                                <div className="text-sm text-emerald-700">
                                    Fit and available.
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <div className="text-sm font-medium">Key Skills</div>
                    </div>
                    <div className="grid gap-2 md:grid-cols-3">
                        {[
                            ["pace", selectedPlayer.attributes?.pace ?? 50],
                            ["ballControl", selectedPlayer.attributes?.ballControl ?? 50],
                            ["passing", selectedPlayer.attributes?.passing ?? 50],
                            ["tackling", selectedPlayer.attributes?.tackling ?? 50],
                            ["endurance", selectedPlayer.attributes?.endurance ?? 50],
                            ["morale", selectedPlayer.attributes?.morale ?? 50],
                            ["positioning", selectedPlayer.attributes?.positioning ?? 50],
                            ["shotPower", selectedPlayer.attributes?.shotPower ?? 50],
                            ["jumping", selectedPlayer.attributes?.jumping ?? 50],
                            ["reactions", selectedPlayer.attributes?.reactions ?? 50],
                            ["composure", selectedPlayer.attributes?.composure ?? 50],
                            ["goalkeeping", selectedPlayer.attributes?.goalkeeping ?? (selectedPlayer.role === "GK" ? 68 : 30)],
                        ].map(([label, rawValue]) => {
                            const value = Number(rawValue);
                            const labelText = String(label);
                            return (
                                <AttributeScoreBar
                                    key={`skill-${labelText}`}
                                    label={labelText.charAt(0).toUpperCase() + labelText.slice(1)}
                                    value={value}
                                />
                            );
                        })}
                    </div>
                    <div className="space-y-3 pt-1 text-sm text-muted-foreground">
                        <div className="grid gap-3 md:grid-cols-2">
                            <label className="space-y-1">
                                <span className="text-xs">Plan</span>
                                <select
                                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                                    value={state.trainingPlanDraft}
                                    onChange={(event) => state.setTrainingPlanDraft(event.target.value)}
                                >
                                    <option value="balanced">Balanced</option>
                                    <option value="fitness">Fitness</option>
                                    <option value="technical">Technical</option>
                                    <option value="tactical">Tactical</option>
                                    <option value="recovery">Recovery</option>
                                </select>
                            </label>
                            <label className="space-y-1">
                                <span className="text-xs">Intensity (0-4)</span>
                                <input
                                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                                    type="number"
                                    min={0}
                                    max={4}
                                    value={state.trainingIntensityDraft}
                                    onChange={(event) => state.setTrainingIntensityDraft(Math.max(0, Math.min(4, Number(event.target.value) || 0)))}
                                />
                            </label>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button variant="outline" onClick={() => void commands.squadSetTrainingPlan(selectedPlayer.id, state.trainingPlanDraft)}>Apply Plan</Button>
                            <Button variant="outline" onClick={() => void commands.squadSetTrainingIntensity(selectedPlayer.id, state.trainingIntensityDraft)}>Apply Intensity</Button>
                        </div>
                        <div className="grid gap-3 md:grid-cols-[1fr_auto_auto]">
                            <label className="space-y-1">
                                <span className="text-xs">Transfer List Price</span>
                                <input
                                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                                    type="number"
                                    min={50000}
                                    step={50000}
                                    value={state.salePriceDraft}
                                    onChange={(event) => state.setSalePriceDraft(Math.max(50000, Number(event.target.value) || 50000))}
                                />
                            </label>
                            <Button variant="outline" className="self-end" onClick={() => void commands.squadListPlayer(selectedPlayer.id, state.salePriceDraft)}>
                                {selectedPlayer.listedForTransfer ? "Update List Price" : "List Player"}
                            </Button>
                            <Button variant="outline" className="self-end" onClick={() => void commands.squadUnlistPlayer(selectedPlayer.id)} disabled={!selectedPlayer.listedForTransfer}>Unlist</Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
            <div className="grid gap-4">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Player Snapshot</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-2 text-sm text-muted-foreground">
                        <p>Role focus: {selectedPlayer.role}</p>
                        <p>
                            Strongest attribute: {strongestAttribute ? `${formatAttributeLabel(strongestAttribute[0])} (${strongestAttribute[1]})` : "No detailed report yet"}
                        </p>
                        <p>
                            Improvement area: {weakestAttribute ? `${formatAttributeLabel(weakestAttribute[0])} (${weakestAttribute[1]})` : "No detailed report yet"}
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
